import React from 'react';

const ScheduleDateCell = ({ dateInfo }) => (
  <td className={`
    px-2 py-2 text-center border-r dark:border-gray-600 whitespace-nowrap
    ${dateInfo.type === 'ACT'
      ? 'text-green-600 dark:text-green-400 font-medium'
      : 'text-gray-900 dark:text-gray-100'
    }`}
  >
    {dateInfo.text}
  </td>
);

const DefaultVesselHeader = ({ vessel }) => (
  <div>
    <div className="px-2 py-1 font-medium text-gray-900 dark:text-white">
      {vessel.vesselName || vessel.name}<br/>{vessel.voyageNumber || vessel.voyage}
    </div>
    <div className="grid grid-cols-2 border-t dark:border-gray-600">
      <div className="px-2 py-1 border-r dark:border-gray-600 text-xs font-medium text-gray-600 dark:text-gray-300">ETA</div>
      <div className="px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300">ETD</div>
    </div>
  </div>
);

const ScheduleGrid = ({ vessels, loadPorts, dischargePorts, VesselHeaderComponent }) => {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg w-full">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm border-collapse table-fixed">
          <colgroup>
            <col className="w-[120px]" />
            {vessels.map((vessel) => (
              <React.Fragment key={`${vessel.imo}-${vessel.voyage}`}>
                <col className="w-[70px]" />
                <col className="w-[70px]" />
              </React.Fragment>
            ))}
          </colgroup>
          <thead>
            <tr>
              <th className="px-3 py-2 text-left border-b border-r dark:border-gray-600 bg-gray-50 dark:bg-gray-800 font-medium sticky left-0 text-gray-900 dark:text-gray-100">Ports</th>
              {vessels.map((vessel, idx) => (
                <th key={`${vessel.imo}-${vessel.voyage}`} colSpan="2" className="text-center border-b border-r dark:border-gray-600 bg-gray-50 dark:bg-gray-800">
                  {VesselHeaderComponent ? (
                    <VesselHeaderComponent vessel={vessel} loadPorts={loadPorts} vesselIndex={idx} />
                  ) : (
                    <DefaultVesselHeader vessel={vessel} />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="text-xs">
            <tr>
              <td colSpan={vessels.length * 2 + 1} className="px-3 py-2 text-blue-600 dark:text-blue-400 font-medium bg-blue-50 dark:bg-blue-900/20 border-b dark:border-gray-600">
                Load Ports
              </td>
            </tr>
            {loadPorts.map((port, portIdx) => (
              <tr key={port.name || portIdx} className="border-b dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-3 py-2 border-r dark:border-gray-600 font-medium sticky left-0 bg-white dark:bg-gray-800 text-gray-900 dark:text-white">{port.name}</td>
                {vessels.map((vessel, vesselIdx) => {
                  let schedule = vessel.loadSchedules?.[portIdx];
                  if (schedule === undefined) {
                    schedule = port.schedules?.[vesselIdx];
                  }
                  let eta = { text: '-', type: '' };
                  let etd = { text: '-', type: '' };

                  if (schedule) {
                    if (schedule.eta && schedule.etd) {
                      eta = schedule.eta;
                      etd = schedule.etd;
                    } else if (typeof schedule === 'object') {
                      if (schedule.text !== undefined) {
                        eta = { text: '-', type: '' };
                        etd = schedule;
                      } else {
                        eta = {
                          text: schedule.etaText || schedule.eta || '-',
                          type: (schedule.etaType === 'ACT' || schedule.etaIsActual === true) ? 'ACT' : 'EST'
                        };
                        etd = {
                          text: schedule.etdText || schedule.etd || schedule.text || '-',
                          type: (schedule.etdType === 'ACT' || schedule.etdIsActual === true) ? 'ACT' : 'EST'
                        };
                      }
                    } else {
                      eta = { text: '-', type: '' };
                      etd = { text: typeof schedule === 'string' ? schedule : '-', type: '' };
                    }
                  }

                  return (
                    <React.Fragment key={`${vessel.imo}-${vessel.voyage}`}>
                      <ScheduleDateCell dateInfo={eta} />
                      <ScheduleDateCell dateInfo={etd} />
                    </React.Fragment>
                  );
                })}
              </tr>
            ))}
            <tr>
              <td colSpan={vessels.length * 2 + 1} className="px-3 py-2 text-green-600 dark:text-green-400 font-medium bg-green-50 dark:bg-green-900/20 border-b dark:border-gray-600">
                Discharge Ports
              </td>
            </tr>
            {dischargePorts.map((port, portIdx) => (
              <tr key={port.key || port.name || portIdx} className="border-b dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-3 py-2 border-r dark:border-gray-600 font-medium sticky left-0 bg-white dark:bg-gray-800 text-gray-900 dark:text-white">{port.name}</td>
                {vessels.map((vessel, vesselIdx) => {
                  let schedule = vessel.dischargeSchedules?.[portIdx];
                  if (schedule === undefined) {
                    schedule = port.schedules?.[vesselIdx];
                  }
                  let eta = { text: '-', type: '' };
                  let etd = { text: '-', type: '' };

                  if (schedule) {
                    if (schedule.eta && schedule.etd) {
                      eta = schedule.eta;
                      etd = schedule.etd;
                    } else if (typeof schedule === 'object') {
                      if (schedule.text !== undefined) {
                        eta = { text: '-', type: '' };
                        etd = schedule;
                      } else {
                        eta = {
                          text: schedule.etaText || schedule.eta || '-',
                          type: (schedule.etaType === 'ACT' || schedule.etaIsActual === true) ? 'ACT' : 'EST'
                        };
                        etd = {
                          text: schedule.etdText || schedule.etd || schedule.text || '-',
                          type: (schedule.etdType === 'ACT' || schedule.etdIsActual === true) ? 'ACT' : 'EST'
                        };
                      }
                    } else {
                      eta = { text: '-', type: '' };
                      etd = { text: typeof schedule === 'string' ? schedule : '-', type: '' };
                    }
                  }

                  return (
                    <React.Fragment key={`${vessel.imo}-${vessel.voyage}`}>
                      <ScheduleDateCell dateInfo={eta} />
                      <ScheduleDateCell dateInfo={etd} />
                    </React.Fragment>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ScheduleGrid;
